import * as Yup from "yup";
import { AttributeFormState } from "@idw-types/state/attribute-form-state";
import { Formik, FormikErrors, FormikProps, FormikTouched } from "formik";
import { SSN9AreaNumberInput } from "./area-number-input";
import { SSN9GroupNumberInput } from "./group-number-input";
import { SSN9SerialNumberInput } from "./serial-number-input";
import { FocusEvent, useEffect, useMemo, useRef, useState } from "react";
import { getIndividualSSNComponents, getSSNValue } from "@utils/attribute-data-util";
import styles from "./ssn9-input-container.less";

type SSN9InputContainerProps = {
    attribute: AttributeFormState;
    fieldName: string;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
    onBlur: (event: FocusEvent<HTMLInputElement>) => void;
    onSetSubmitValue: (data: any) => void;
    isSubmitting: boolean;
};

export const SSN9InputContainer = (props: SSN9InputContainerProps) => {
    const {
        attribute,
        fieldName,
        setFieldValue,
        onBlur,
        onSetSubmitValue,
        isSubmitting
    } = props;

    const isRequired = useMemo(() => !(attribute.isOptional || attribute.optional), [attribute.isOptional, attribute.optional]);
    const [formInitialValue] = useState({ ...attribute.inputData });
    const schema = useMemo(() => {
        return isRequired ?
            Yup.object().shape({
                areaNumber: Yup.string()
                    .test('isNumber', 'validation.areaNumber.isNumber', (value: string) => /^\d+$/.test(value))
                    .required('validation.areaNumber.required')
                    .length(3, 'validation.areaNumber.length'),
                groupNumber: Yup.string()
                    .test('isNumber', 'validation.groupNumber.isNumber', (value: string) => /^\d+$/.test(value))
                    .required('validation.groupNumber.required')
                    .length(2, 'validation.groupNumber.length'),
                serialNumber: Yup.string()
                    .test('isNumber', 'validation.serialNumber.isNumber', (value: string) => /^\d+$/.test(value))
                    .length(4, 'validation.serialNumber.length')
                    .required('validation.serialNumber.required')
            })
            : null;
    }, [isRequired]);

    const areaNumberInputRef = useRef<HTMLInputElement>(null);
    const groupNumberInputRef = useRef<HTMLInputElement>(null);
    const serialNumberInputRef = useRef<HTMLInputElement>(null);
    const [isTyping, setIsTyping] = useState(false);

    const initializeFromInputData = async () => {
        const ssnComponents = getIndividualSSNComponents(attribute.inputData.ssn9);
        onSetSubmitValue({ inputData: ssnComponents });
        await setFieldValue(fieldName, ssnComponents.ssn9);
        await setFieldValue("areaNumber", ssnComponents.areaNumber);
        await setFieldValue("groupNumber", ssnComponents.groupNumber);
        await setFieldValue("serialNumber", ssnComponents.serialNumber);
    };

    useEffect(() => {
        const inputDataKeys = Object.keys(attribute.inputData);
        if (inputDataKeys.length === 1 && inputDataKeys[0] === "ssn9") {
            initializeFromInputData();
        }
    }, []);

    useEffect(() => {
        const { areaNumber, groupNumber, serialNumber } = attribute.inputData;
        const ssn9 = getSSNValue(areaNumber, groupNumber, serialNumber);
        setFieldValue(fieldName, ssn9);
        setIsTyping(true);
    }, [attribute.inputData.areaNumber, attribute.inputData.groupNumber, attribute.inputData.serialNumber]);

    useEffect(() => {
        if (document.activeElement === areaNumberInputRef?.current && isTyping && attribute.inputData.areaNumber?.length === 3) {
            setIsTyping(false);
            groupNumberInputRef.current?.focus();
        } else if (document.activeElement === groupNumberInputRef.current && isTyping && attribute.inputData.groupNumber?.length === 2) {
            setIsTyping(false);
            serialNumberInputRef.current?.focus();
        }
    }, [
        document.activeElement,
        isTyping,
        areaNumberInputRef,
        groupNumberInputRef,
        serialNumberInputRef,
        attribute.inputData.areaNumber,
        attribute.inputData.groupNumber
    ]);

    const inputType = useMemo(() => {
        return attribute.masked ? "password" : "number";
    }, [attribute.masked]);

    const renderSSN9InputContainer = (
        errors: FormikErrors<any>,
		touched: FormikTouched<any>,
		setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void,
		setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>
    ) => {
        return (
            <div className={styles.ssn9InputContainer}>
                <SSN9AreaNumberInput
                    attribute={attribute}
                    inputType={inputType}
                    errors={errors}
                    onBlur={onBlur}
                    setFieldTouched={setFieldTouched}
                    setFieldValue={setFieldValue}
                    touched={touched}
                    isSubmitting={isSubmitting}
                    onSetSubmitValue={onSetSubmitValue}
                    inputRef={areaNumberInputRef}
                />
                <SSN9GroupNumberInput
                    attribute={attribute}
                    inputType={inputType}
                    errors={errors}
                    onBlur={onBlur}
                    setFieldTouched={setFieldTouched}
                    setFieldValue={setFieldValue}
                    touched={touched}
                    isSubmitting={isSubmitting}
                    onSetSubmitValue={onSetSubmitValue}
                    inputRef={groupNumberInputRef}
                />
                <SSN9SerialNumberInput
                    attribute={attribute}
                    inputType={inputType}
                    errors={errors}
                    onBlur={onBlur}
                    setFieldTouched={setFieldTouched}
                    setFieldValue={setFieldValue}
                    touched={touched}
                    isSubmitting={isSubmitting}
                    onSetSubmitValue={onSetSubmitValue}
                    inputRef={serialNumberInputRef}
                />
            </div>
        );
    };

    if (!attribute.isNew) {
        return null;
    }

    return (
        <Formik
            initialValues={formInitialValue}
            validateOnMount={true}
            validationSchema={schema}
            onSubmit={undefined}
        >
            {({ errors, touched, setFieldTouched, setFieldValue }: FormikProps<any>) => (
                renderSSN9InputContainer(
                    errors,
                    touched,
                    setFieldTouched,
                    setFieldValue
                )
            )}
        </Formik>
    );
};